import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { api, mtlToday, fmtDate } from "@/lib/api";
import { SectionHead } from "@/components/site/Contact";

const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };

export function DayTimeline({ busy }) {
  const { t } = useLang();
  return (
    <div>
      <div className="relative h-10 overflow-hidden rounded-lg border border-white/10 bg-emerald-500/10" data-testid="availability-timeline">
        {busy.map((b, i) => (
          <div key={i} title={`${b.start}–${b.end}`}
            className={`absolute top-0 h-full ${b.kind === "maintenance" ? "bg-amber-500/60" : b.mine ? "bg-sky-500/70" : "bg-red-600/80"}`}
            style={{ left: `${(toMin(b.start) / 1440) * 100}%`, width: `${((toMin(b.end) - toMin(b.start)) / 1440) * 100}%` }} />
        ))}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500">
        {["00", "06", "12", "18", "24"].map((h) => <span key={h}>{h}:00</span>)}
      </div>
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-emerald-500/40" />{t("avail.free")}</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-red-600" />{t("avail.busy")}</span>
        <span className="flex items-center gap-1.5"><i className="h-2.5 w-2.5 rounded-sm bg-amber-500" />{t("avail.maint")}</span>
      </div>
    </div>
  );
}

export default function Availability() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const days = Array.from({ length: 7 }, (_, i) => mtlToday(i));
  const [day, setDay] = useState(days[0]);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/public/availability", { params: { date: day, duration: 1 } }).then((r) => setData(r.data)).catch(() => setData(null));
  }, [day]);

  return (
    <section id="availability" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-10 lg:py-28" data-testid="availability-section">
      <SectionHead eyebrow={t("avail.eyebrow")} title={t("avail.title")} sub={t("avail.sub")} />
      <div className="panel p-5 sm:p-8">
        <div className="-mx-1 mb-6 flex gap-2 overflow-x-auto px-1 pb-2">
          {days.map((d, i) => (
            <button key={d} onClick={() => setDay(d)} data-testid={`availability-day-${i}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm capitalize transition-colors ${d === day ? "border-red-500 bg-red-600 text-white" : "border-white/10 text-slate-300 hover:border-white/30"}`}>
              {fmtDate(d, lang)}
            </button>
          ))}
        </div>
        <DayTimeline busy={data?.busy || []} />
        <div className="mt-6">
          {data && data.busy.length === 0 ? (
            <p className="text-sm text-emerald-300" data-testid="availability-all-free">{t("avail.none")}</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="availability-busy-list">
              {(data?.busy || []).map((b, i) => (
                <span key={i} className="rounded-md border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-xs text-slate-300">
                  {b.start}–{b.end} · {b.kind === "maintenance" ? t("avail.maint") : t("avail.busy")}
                </span>
              ))}
            </div>
          )}
        </div>
        <p className="mt-4 text-xs text-slate-500">{lang === "fr" ? "Aperçu en lecture seule. Les membres actifs réservent dans leur espace membre." : "Read-only overview. Active members book from their member area."}</p>
        {!user ? (
          <a href="#memberships" data-testid="availability-choose-plan-btn" className="btn-red mt-6 inline-block rounded-full px-6 py-3 font-semibold">{t("avail.cta")}</a>
        ) : user.role === "member" && (
          <Link to="/member?tab=book" data-testid="availability-book-btn" className="btn-red mt-6 inline-block rounded-full px-6 py-3 font-semibold">{lang === "fr" ? "Réserver une séance au studio" : "Book a Studio Session"}</Link>
        )}
      </div>
    </section>
  );
}
